import React, { useRef, useState } from "react";
import api from "../api";
import { Upload, X, Loader2 } from "lucide-react";

/**
 * Upload one image and surface the resulting public URL.
 * - props.value (current image URL) + props.onChange(url)
 * - Falls back to manual URL paste (textbox) for cases where the user
 *   prefers an external image (e.g. Wikipedia, their own CDN).
 */
export default function ImageUploader({ value, onChange, label, aspect = "square", testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  async function handleFile(file) {
    if (!file) return;
    setErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  const previewBox = (
    <div
      onClick={() => fileRef.current?.click()}
      className="relative cursor-pointer flex items-center justify-center"
      style={{
        width: "100%",
        aspectRatio: aspect === "cover" ? "16 / 9" : aspect === "wide" ? "21 / 9" : "1 / 1",
        background: "var(--bg)",
        border: "1px dashed var(--line)",
        overflow: "hidden",
      }}
      data-testid={testid}
    >
      {value ? (
        <>
          <img src={value} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="absolute top-2 right-2 p-1"
            style={{ background: "var(--ink)", color: "white" }}
            data-testid={`${testid}-clear`}
          >
            <X size={12} />
          </button>
        </>
      ) : uploading ? (
        <div className="text-center">
          <Loader2 size={20} className="animate-spin mx-auto" />
          <div className="overline mt-2">UPLOADING…</div>
        </div>
      ) : (
        <div className="text-center" style={{ color: "var(--ink-2)" }}>
          <Upload size={20} className="mx-auto" />
          <div className="overline mt-2">CLICK TO UPLOAD</div>
          <div className="font-mono text-xs mt-1">JPG · PNG · WEBP · 6MB</div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {label ? <label className="overline">{label}</label> : null}
      <div className="mt-2">{previewBox}</div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testid}-input`}
      />
      <input
        type="text"
        placeholder="…or paste an image URL"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="cd-input mt-2 text-xs"
        data-testid={`${testid}-url`}
      />
      {err ? (
        <div className="mt-2 font-mono text-xs px-2 py-1" style={{ background: "var(--red)", color: "white" }}>
          {err}
        </div>
      ) : null}
    </div>
  );
}

/** Multi-image gallery uploader */
export function GalleryUploader({ value = [], onChange, label, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  async function handleFiles(files) {
    if (!files || files.length === 0) return;
    setErr(""); setUploading(true);
    try {
      const urls = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        urls.push(data.url);
      }
      onChange([...(value || []), ...urls]);
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function remove(url) {
    onChange((value || []).filter((u) => u !== url));
  }

  return (
    <div>
      {label ? <label className="overline">{label}</label> : null}
      <div className="mt-2 grid grid-cols-3 gap-2">
        {(value || []).map((u, i) => (
          <div key={i} className="relative" style={{ aspectRatio: "1 / 1", overflow: "hidden", border: "1px solid var(--line)" }}>
            <img src={u} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <button
              type="button"
              onClick={() => remove(u)}
              className="absolute top-1 right-1 p-1"
              style={{ background: "var(--ink)", color: "white" }}
              data-testid={`${testid}-remove-${i}`}
            >
              <X size={10} />
            </button>
          </div>
        ))}
        <div
          onClick={() => fileRef.current?.click()}
          className="flex items-center justify-center cursor-pointer"
          style={{ aspectRatio: "1 / 1", background: "var(--bg)", border: "1px dashed var(--line)", color: "var(--ink-2)" }}
          data-testid={`${testid}-add`}
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testid}-input`}
      />
      {err ? (
        <div className="mt-2 font-mono text-xs px-2 py-1" style={{ background: "var(--red)", color: "white" }}>
          {err}
        </div>
      ) : null}
    </div>
  );
}
